import type { CaseEvent, Client, EventType } from '@core/domain/model';
import { time, weekdayIndex } from '@core/ui/RuDates';

/**
 * Сетка месяца и содержимое дня — порт `CalendarFormatter` и `buildState`.
 *
 * Чистые функции без базы и без React: на них держится весь экран, и проверяются они
 * теми же случаями, что в Android-тестах.
 */

/** Событие в списке дня — «Структура» §9.1. */
export interface CalendarEventRow {
  id: string;
  title: string;
  /** «Заседание · Иванов — раздел имущества». */
  subtitle: string;
  time: string;
  type: EventType;
  isCompleted: boolean;
  /**
   * День рождения клиента: это не запись в базе событий, а вычисляемая отметка,
   * поэтому открыть её как событие нельзя — только перейти к клиенту.
   */
  clientId?: string;
}

/** День в сетке месяца. */
export interface CalendarDay {
  /** Полночь этого дня по местному времени — им же день и опознаётся. */
  date: number;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  /** Есть ли заседание: точка под числом красится по самому важному типу. */
  hasHearing: boolean;
  hasOtherEvents: boolean;
  hasBirthday: boolean;
}

export const startOfDay = (moment: number): number => {
  const at = new Date(moment);
  at.setHours(0, 0, 0, 0);

  return at.getTime();
};

export const startOfMonth = (moment: number): number => {
  const at = new Date(moment);
  at.setDate(1);
  at.setHours(0, 0, 0, 0);

  return at.getTime();
};

export const shiftMonth = (month: number, by: number): number => {
  const at = new Date(month);
  at.setMonth(at.getMonth() + by, 1);

  return at.getTime();
};

const sameMonth = (left: number, right: number) => {
  const a = new Date(left);
  const b = new Date(right);

  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
};

/**
 * Сетка месяца с понедельника, добитая соседними месяцами до полных недель.
 *
 * Неполная первая строка ломает чтение календаря: глаз ищет столбец дня недели,
 * а не порядковый номер клетки.
 */
export function gridOf(month: number): number[] {
  const first = new Date(startOfMonth(month));
  const start = new Date(first);
  start.setDate(first.getDate() - weekdayIndex(first));

  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - weekdayIndex(last)));

  const days: number[] = [];
  for (let at = new Date(start); at <= end; at.setDate(at.getDate() + 1)) {
    days.push(startOfDay(at.getTime()));
  }
  return days;
}

/** День рождения в этом году: 29 февраля в невисокосный год отмечаем 28-го. */
export function birthdayIn(birthDate: string, year: number): number | null {
  const [, month, day] = birthDate.split('-').map(Number);
  if (!month || !day) return null;

  // Нулевой день следующего месяца — это последний день текущего.
  const lengthOfMonth = new Date(year, month, 0).getDate();

  return new Date(year, month - 1, Math.min(day, lengthOfMonth)).getTime();
}

const TYPE_LABEL: Record<EventType, string> = {
  Hearing: 'Заседание',
  Meeting: 'Встреча',
  Task: 'Задача',
  Other: 'Событие',
};

export const eventTypeLabel = (type: EventType): string => TYPE_LABEL[type];

export function toRow(event: CaseEvent, caseTitle: string | null): CalendarEventRow {
  return {
    id: event.id,
    title: event.title,
    subtitle: [TYPE_LABEL[event.type], caseTitle].filter(Boolean).join(' · '),
    time: time(new Date(event.startAt)),
    type: event.type,
    isCompleted: event.isCompleted,
  };
}

/**
 * День рождения клиента — «Структура» §9.1.
 *
 * В базе событий его нет и быть не должно: это свойство клиента, повторяющееся каждый
 * год. Календарь вычисляет отметку сам, поэтому её нельзя ни отметить выполненной,
 * ни удалить — только перейти к клиенту.
 */
export function birthdayRow(client: Client, year: number): CalendarEventRow {
  const born = client.birthDate ? Number(client.birthDate.slice(0, 4)) : null;

  return {
    id: `birthday:${client.id}:${year}`,
    title: client.fullName,
    subtitle: born ? `День рождения · ${year - born}` : 'День рождения',
    time: '',
    type: 'Other',
    isCompleted: false,
    clientId: client.id,
  };
}

/** Дни месяца с отметками о событиях и днях рождения. */
export function daysOf(
  month: number,
  selected: number,
  today: number,
  events: CaseEvent[],
  clients: Client[],
): CalendarDay[] {
  const byDate = new Map<number, CaseEvent[]>();
  for (const event of events) {
    const day = startOfDay(event.startAt);
    byDate.set(day, [...(byDate.get(day) ?? []), event]);
  }

  const year = new Date(month).getFullYear();
  const birthdays = new Set(
    clients
      .map((client) => (client.birthDate ? birthdayIn(client.birthDate, year) : null))
      .filter((day): day is number => day !== null),
  );

  return gridOf(month).map((date) => {
    const ofDay = byDate.get(date) ?? [];

    return {
      date,
      dayOfMonth: new Date(date).getDate(),
      isCurrentMonth: sameMonth(date, month),
      isToday: date === startOfDay(today),
      isSelected: date === startOfDay(selected),
      hasHearing: ofDay.some((event) => event.type === 'Hearing'),
      hasOtherEvents: ofDay.some((event) => event.type !== 'Hearing'),
      hasBirthday: birthdays.has(date),
    };
  });
}

/**
 * Что показывает список выбранного дня.
 *
 * Дни рождения идут первыми и без времени: это отметка дня, а не встреча в 10:30.
 */
export function eventsOfDay(
  selected: number,
  events: CaseEvent[],
  clients: Client[],
  caseTitles: Map<string, string>,
): CalendarEventRow[] {
  const day = startOfDay(selected);
  const year = new Date(day).getFullYear();

  const birthdays = clients
    .filter((client) => client.birthDate && birthdayIn(client.birthDate, year) === day)
    .map((client) => birthdayRow(client, year));

  const ofDay = events
    .filter((event) => startOfDay(event.startAt) === day)
    .sort((a, b) => a.startAt - b.startAt)
    .map((event) => toRow(event, caseTitles.get(event.caseId ?? '') ?? null));

  return [...birthdays, ...ofDay];
}
